import React, {
  useState,
  useEffect,
  ComponentType,
  ReactNode,
  useMemo,
} from 'react';

import {
  fetchWithTokenAndRegion,
  getAdminConstructionApiUrl,
  getCurrentToken,
  getCurrentTokenWithoutRedirect,
  getOxygenId,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { SubscriptionConstruction } from './types';

type SubscriptionsConstructionContextValue = {
  data: null | SubscriptionConstruction[]; //  change this
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

type SubscriptionsV2ProviderProps = {
  children?: ReactNode;
};

/**
 * Context for current user subscriptions construction version.
 */
const SubscriptionsConstructionContext =
  getOrCreateGlobalContext<SubscriptionsConstructionContextValue>(
    CONTEXT_NAMES.SubscriptionsConstructionContext,
  );

const useSubscriptionsConstructionContext = () =>
  useContextWithValidation(SubscriptionsConstructionContext, {
    hook: 'useSubscriptionsConstruction',
    provider: 'SubscriptionsConstructionProvider',
  });

export const useSubscriptionsConstruction = (enable = true) => {
  const { setEnabled, ...rest } = useSubscriptionsConstructionContext();
  useEffect(() => {
    if (enable && setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled, enable]);
  return rest;
};

const fetchConstructionSubscriptions = () =>
  getCurrentToken().then(({ accessToken }) => {
    const oxygenId = getOxygenId(accessToken);
    return fetchWithTokenAndRegion<{
      results: SubscriptionConstruction[];
    }>(
      `${getAdminConstructionApiUrl()}/internal/v1/users/${oxygenId}/subscriptions?filter[status]=active,expired`,
    );
  });

export const SubscriptionsConstructionProvider: ComponentType<
  SubscriptionsV2ProviderProps
> = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const {
    data: fetchData,
    error,
    revalidate: retry,
  } = useSWRAlloy(
    enabled ? '/current_user_subscriptions_construction' : null,
    () =>
      getCurrentTokenWithoutRedirect().then(() =>
        fetchConstructionSubscriptions(),
      ),
  );
  const data = error?.status === 401 ? null : fetchData;
  const loading = !data && !error;

  const setEnabledValue = useMemo(() => {
    if (enabled) return undefined;

    return setEnabled;
  }, [enabled, setEnabled]);

  return (
    <SubscriptionsConstructionContext.Provider
      value={{
        error,
        loading,
        data: data?.results,
        retry,
        setEnabled: setEnabledValue,
      }}
    >
      {children}
    </SubscriptionsConstructionContext.Provider>
  );
};

SubscriptionsConstructionProvider.displayName =
  'SubscriptionsConstructionProvider';
